import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { AlertCircle, CheckCircle, Eye, EyeOff, Mail } from 'lucide-react';

function parseHashParams(hash: string): Record<string, string> {
  const params: Record<string, string> = {};
  if (!hash) return params;
  const cleanHash = hash.startsWith('#') ? hash.slice(1) : hash;
  for (const part of cleanHash.split('&')) {
    const [key, ...rest] = part.split('=');
    if (key) params[decodeURIComponent(key)] = decodeURIComponent(rest.join('='));
  }
  return params;
}

function getErrorMessage(errorCode: string, errorDescription: string): string {
  switch (errorCode) {
    case 'otp_expired':
      return 'Lenken har utløpt eller er allerede brukt. Noen e-postklienter (Outlook, Gmail) forhåndslaster lenker automatisk, noe som kan bruke opp engangskoden. Be om en ny lenke nedenfor.';
    case 'access_denied':
      return errorDescription
        ? decodeURIComponent(errorDescription.replace(/\+/g, ' '))
        : 'Tilgang nektet. Lenken kan være ugyldig eller utløpt.';
    default:
      return errorDescription
        ? decodeURIComponent(errorDescription.replace(/\+/g, ' '))
        : 'Lenken er ugyldig eller har utløpt. Be om en ny lenke.';
  }
}

export function ResetPassword() {
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  const [checking, setChecking] = useState(true);
  const [linkExpired, setLinkExpired] = useState(false);
  const [expiredMessage, setExpiredMessage] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const hash = window.location.hash;

    // Check for errors in query params (PKCE) or hash (implicit legacy)
    const errorInQuery = searchParams.get('error');
    const errorInHash = hash && hash.includes('error=');

    if (errorInQuery || errorInHash) {
      let errorCode: string;
      let errorDescription: string;
      if (errorInQuery) {
        errorCode = searchParams.get('error_code') || searchParams.get('error') || '';
        errorDescription = searchParams.get('error_description') || '';
      } else {
        const hashParams = parseHashParams(hash);
        errorCode = hashParams['error_code'] || hashParams['error'] || '';
        errorDescription = hashParams['error_description'] || '';
      }
      console.warn('🔑 Reset link error:', { errorCode, errorDescription });
      setExpiredMessage(getErrorMessage(errorCode, errorDescription));
      setLinkExpired(true);
      setChecking(false);
      return;
    }

    let resolved = false;
    const resolve = () => {
      if (resolved) return;
      resolved = true;
      setHasSession(true);
      setChecking(false);
    };

    // Listen for PASSWORD_RECOVERY event (fired after PKCE code exchange or implicit token)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('🔑 ResetPassword auth event:', event);
      if (event === 'PASSWORD_RECOVERY' || (event === 'SIGNED_IN' && session)) {
        resolve();
      }
    });

    const hasCode = searchParams.has('code');
    const hasToken = hash && (hash.includes('access_token') || hash.includes('type=recovery'));

    if (hasCode) {
      // PKCE flow: Supabase client exchanges the code automatically via detectSessionInUrl.
      // Just wait for the PASSWORD_RECOVERY event, with a timeout fallback.
      const timeout = setTimeout(async () => {
        if (resolved) return;
        // Fallback: check if session was established
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session) { resolve(); return; }
        } catch { /* ignore */ }
        // Code exchange may have failed
        setExpiredMessage(getErrorMessage('otp_expired', ''));
        setLinkExpired(true);
        setChecking(false);
      }, 8000);

      return () => {
        clearTimeout(timeout);
        subscription.unsubscribe();
      };
    } else if (hasToken) {
      // Legacy implicit flow fallback
      const hashParams = parseHashParams(hash);
      const accessToken = hashParams['access_token'];
      const refreshToken = hashParams['refresh_token'];

      const setSessionManually = async () => {
        if (!accessToken || !refreshToken) return false;
        try {
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (!error && data.session) {
            window.location.hash = '';
            resolve();
            return true;
          }
        } catch (err) {
          console.warn('🔑 Manual setSession failed:', err);
        }
        return false;
      };

      (async () => {
        await new Promise(r => setTimeout(r, 1500));
        if (resolved) return;
        if (await setSessionManually()) return;
        await new Promise(r => setTimeout(r, 2000));
        if (resolved) return;
        if (await setSessionManually()) return;
        setChecking(false);
      })();

      return () => { subscription.unsubscribe(); };
    } else {
      // No code or token — check existing session
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session) resolve();
        else if (!resolved) setChecking(false);
      }).catch(() => {
        if (!resolved) setChecking(false);
      });
    }

    return () => { subscription.unsubscribe(); };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passordene stemmer ikke overens');
      return;
    }

    if (newPassword.length < 8) {
      setError('Passordet må være minst 8 tegn');
      return;
    }

    setLoading(true);

    try {
      // Verify we have an active session before attempting password update
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setError('Sesjonen har utløpt. Be om en ny tilbakestillingslenke.');
        setLoading(false);
        return;
      }

      const { data, error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        console.error('🔑 Password update error:', error);
        if (error.message.includes('same_password') || error.message.includes('different from the old')) {
          setError('Det nye passordet kan ikke være det samme som det gamle.');
        } else if (error.message.includes('session_not_found') || error.message.includes('not authenticated')) {
          setError('Sesjonen har utløpt. Be om en ny tilbakestillingslenke.');
        } else {
          setError(error.message);
        }
      } else {
        setSuccess(true);
        // Sign out and redirect to login immediately
        // signOut first, then redirect regardless of outcome
        try {
          await supabase.auth.signOut();
        } catch {
          // Ignore signOut errors
        }
        // Short delay to show success message, then hard redirect
        setTimeout(() => {
          window.location.href = '/login';
        }, 1500);
      }
    } catch (err: any) {
      console.error('🔑 Password update exception:', err);
      setError(err.message || 'En feil oppstod ved tilbakestilling av passord');
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <p className="text-gray-500 dark:text-gray-400">Laster...</p>
      </div>
    );
  }

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resendEmail) return;
    setResendLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resendEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) {
        setError(error.message);
      } else {
        setResendSuccess(true);
      }
    } catch {
      setError('Kunne ikke sende e-post. Prøv igjen.');
    } finally {
      setResendLoading(false);
    }
  };

  if (!hasSession || linkExpired) {
    return (
      <div className="min-h-[80vh] flex flex-col items-center justify-center">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-8">
            <img src="/logo_light.png" alt="Helsenøkkelen" className="h-12 w-auto dark:hidden" />
            <img src="/logo_dark.png" alt="Helsenøkkelen" className="h-12 w-auto hidden dark:block" />
          </div>

          <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-sm border dark:border-gray-700 text-center">
            <AlertCircle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">
              {linkExpired ? 'Lenken har utløpt' : 'Ugyldig eller utløpt lenke'}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              {expiredMessage || 'Tilbakestillingslenken er ugyldig eller har utløpt. Be om en ny lenke.'}
            </p>

            {resendSuccess ? (
              <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md flex items-start gap-3 text-left">
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-900 dark:text-green-100 mb-1">
                    Ny lenke sendt!
                  </p>
                  <p className="text-sm text-green-700 dark:text-green-300">
                    Sjekk innboksen din for <strong>{resendEmail}</strong>. Klikk lenken i e-posten innen noen minutter.
                  </p>
                  <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                    Tips: Hvis lenken ikke fungerer, prøv å kopiere URL-en fra e-posten og lime den direkte i nettleseren.
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleResend} className="space-y-3 text-left">
                <div>
                  <label htmlFor="resendEmail" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Skriv inn e-posten din for å motta ny lenke
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      id="resendEmail"
                      type="email"
                      required
                      value={resendEmail}
                      onChange={(e) => setResendEmail(e.target.value)}
                      placeholder="din@epost.no"
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={resendLoading}
                  className="w-full px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 disabled:bg-blue-400 disabled:cursor-not-allowed"
                >
                  {resendLoading ? 'Sender...' : 'Send ny tilbakestillingslenke'}
                </button>
              </form>
            )}

            <div className="mt-4 pt-4 border-t dark:border-gray-700">
              <Link
                to="/login"
                className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              >
                Tilbake til innlogging
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex flex-col items-center justify-center">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img
            src="/logo_light.png"
            alt="Helsenøkkelen"
            className="h-12 w-auto dark:hidden"
          />
          <img
            src="/logo_dark.png"
            alt="Helsenøkkelen"
            className="h-12 w-auto hidden dark:block"
          />
        </div>

        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-sm border dark:border-gray-700">
          <h2 className="text-2xl font-bold mb-2 text-gray-900 dark:text-white">
            Tilbakestill passord
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            Skriv inn ditt nye passord.
          </p>

          {success ? (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-900 dark:text-green-100 mb-1">
                    Passordet er tilbakestilt!
                  </p>
                  <p className="text-sm text-green-700 dark:text-green-300">
                    Du blir automatisk videresendt til innloggingssiden...
                  </p>
                </div>
              </div>

              <Link
                to="/login"
                className="block w-full text-center px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600"
              >
                Gå til innlogging
              </Link>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Nytt passord
                  </label>
                  <div className="relative">
                    <input
                      id="newPassword"
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      minLength={8}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Minimum 8 tegn
                  </p>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Bekreft nytt passord
                  </label>
                  <input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    minLength={8}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 dark:bg-blue-500 text-white py-2 rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 disabled:bg-blue-400 dark:disabled:bg-blue-700 disabled:cursor-not-allowed"
                >
                  {loading ? 'Tilbakestiller...' : 'Tilbakestill passord'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
